import PropTypes from 'prop-types';
import React from 'react';

import chroma from 'chroma-js';

const HEX_PITCH = 108; // Distance from centre of one hex to centre of the next
const HEX_MULT = Math.cos(Math.PI / 6); // An oft-used value when calculating points on the hex and hex grid
const HEX_FILL = 0.94 / HEX_MULT; // Where 1 means the points meet the circle with diameter HEX_PITCH, or 1/HEX_MULT means there are no gaps
const HEX_ROWS = 12; // Should be an even number
const HEX_COLS = 9;

const hexRadiusMax = 0.5 * HEX_PITCH * HEX_FILL
const hexRadiusMin = hexRadiusMax * HEX_MULT

function randomBetween(min, max, pdf = x => x) {
	return min + pdf(Math.random()) * (max - min);
}

function favourEdges(x) {
	const a = Math.pow(x, 2);
	if (Math.random() < 0.5) {
		return a;
	}
	return 1 - a;
}

export default class HexGrid extends React.Component {
	static propTypes = {
		hue: PropTypes.number.isRequired,
		hueSpan: PropTypes.number.isRequired,
		saturation: PropTypes.number.isRequired,
		saturationSpan: PropTypes.number.isRequired,
		lightness: PropTypes.number.isRequired,
		lightnessSpan: PropTypes.number.isRequired,
	}

	static defaultProps = {
		hue: 0,
		hueSpan: 0,
		saturation: 100,
		saturationSpan: 0,
		lightness: 50,
		lightnessSpan: 0,
	}

	constructor(props) {
		super(props);

		// Store colours for each hex in state
		this.state = {
			fills: [],
		};
		for (let y = 0; y < HEX_ROWS; y++) {
			const row = [];
			for (let x = 0; x < HEX_COLS; x++) {
				row.push(this.randomSpec());
			}
			this.state.fills.push(row);
		}
	}

	setFills = (data) => {
		const fills = this.state.fills;

		// Collect affected hexes
		const affected = [];

		for (const {x, y, spec} of data) {
			fills[y][x] = spec;
			affected.push([x, y]);

			// Also final row if first row
			if (y === 0) {
				affected.push([x, HEX_ROWS]);
			}

			// Duplicates on first column of even rows
			if (y % 2 === 0 && x === 0) {
				affected.push([HEX_COLS, y]);
				if (y === 0) {
					affected.push([HEX_COLS, HEX_ROWS]);
				}
			}
		}

		this.setState({
			fills: fills,
		});
	}

	setFill = (x, y, spec) => {
		return this.setFills([{x, y, spec}]);
	}

	renderFill({hue, saturation, lightness}) {
		return chroma.hcl(hue, saturation, lightness).hex();
	}

	randomSpec() {
		const {
			hue,
			hueSpan,
			saturation,
			saturationSpan,
			lightness,
			lightnessSpan,
		} = this.props;

		return {
			hue: randomBetween(360 + hue - hueSpan, 360 + hue + hueSpan, favourEdges) % 360,
			saturation: Math.max(0, Math.min(100, randomBetween(saturation - saturationSpan, saturation + saturationSpan))),
			lightness: Math.max(0, Math.min(100, randomBetween(lightness - lightnessSpan, lightness + lightnessSpan))),
		};
	}

	newColours = () => {
		// Cancel any existing animation
		if (this.animation) {
			window.cancelAnimationFrame(this.animation);
		}

		// Set total time for transition
		const total = 1e3;

		// Build a sorted list of hexes by transition start time
		const list = [];
		for (let x = 0; x < HEX_COLS; x++) {
			for (let y = 0; y < HEX_ROWS; y++) {
				list.push({
					x,
					y,
					time: Math.random() * total,
				});
			}
		}
		list.sort((a, b) => a.time - b.time);

		// Start appropriate hexes animating whenever possible
		const startTime = window.performance.now();
		let index = 0;
		const length = list.length;
		const update = (time) => {
			const data = [];
			while (index < length) {
				const item = list[index];
				if (item.time <= time - startTime) {
					data.push({
						x: item.x,
						y: item.y,
						spec: this.randomSpec(),
					});
					index++;
				} else {
					break;
				}
			}
			if (data.length) {
				this.setFills(data);
			}
			if (index < length) {
				this.animation = window.requestAnimationFrame(update);
			}
		}
		this.animation = window.requestAnimationFrame(update);
	}

	componentDidUpdate(oldProps) {
		if (oldProps.hue !== this.props.hue
				|| oldProps.hueSpan !== this.props.hueSpan
				|| oldProps.saturation !== this.props.saturation
				|| oldProps.saturationSpan !== this.props.saturationSpan
				|| oldProps.lightness !== this.props.lightness
				|| oldProps.lightnessSpan !== this.props.lightnessSpan) {
			// Roll out new colours
			this.newColours();
		}
	}

	render() {
		const {
			hue,
			hueSpan,
			saturation,
			saturationSpan,
			lightness,
			lightnessSpan,
			...otherProps
		} = this.props;

		return (
			<svg onClick={this.newColours} {...otherProps}>
				<defs>
					<polygon
						id="hex"
						points={[
							[0, hexRadiusMax],
							[hexRadiusMin, hexRadiusMax * 0.5],
							[hexRadiusMin, -hexRadiusMax * 0.5],
							[0, -hexRadiusMax],
							[-HEX_MULT * hexRadiusMax, -hexRadiusMax * 0.5],
							[-HEX_MULT * hexRadiusMax, hexRadiusMax * 0.5],
						].map(c => c.join(',')).join(' ')}
					/>
					<pattern id="hexgrid-edge" x={-(HEX_PITCH * 0.5 - hexRadiusMin)} y={0} width={HEX_COLS * HEX_PITCH} height={HEX_ROWS * HEX_MULT * HEX_PITCH} patternUnits="userSpaceOnUse">
						{Array(HEX_ROWS + 1).fill(0).map((_, y) => y % 2
							? <Hex
								key={`edge,${y}`}
								fill={this.renderFill(this.state.fills[y % HEX_ROWS][0])}
								x={(y % 2) * HEX_PITCH * 0.5}
								y={y * HEX_PITCH * HEX_MULT}
							/>
							: null
						)}
					</pattern>
					<pattern id="hexgrid" x={-(HEX_PITCH * 0.5 - hexRadiusMin)} y={0} width={HEX_COLS * HEX_PITCH} height={HEX_ROWS * HEX_MULT * HEX_PITCH} patternUnits="userSpaceOnUse">
						{Array(HEX_ROWS + 1).fill(0).map((_, y) =>
							Array(HEX_COLS + 1).fill(0).map((_, x) =>
								<Hex
									key={`${x},${y}`}
									fill={this.renderFill(this.state.fills[y % HEX_ROWS][x % HEX_COLS])}
									x={x * HEX_PITCH + (y % 2) * HEX_PITCH * 0.5}
									y={y * HEX_PITCH * HEX_MULT}
								/>
							)
						)}
					</pattern>
				</defs>
				<rect fill="url(#hexgrid-edge)" width={hexRadiusMin + 1 /* avoid subpixel break; prefer to overlap */} height="100%" x={0} y={0} />
				<rect fill="url(#hexgrid)" width="100%" height="100%" x={hexRadiusMin} y={0} />
			</svg>
		);
	}
}

class Hex extends React.Component {
	static propTypes = {
		fill: PropTypes.string.isRequired,
	}

	constructor(props) {
		super(props);
		this.state = {
			prevFill: null,
		};
	}

	componentWillReceiveProps(newProps) {
		if (newProps.fill && newProps.fill !== this.props.fill) {
			this.setState({
				prevFill: this.props.fill,
			});
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.animation && this.animation.beginElement && this.props.fill !== prevProps.fill && this.state.prevFill !== prevState.prevFill) {
			this.animation.beginElement();
		}
	}

	render() {
		const {
			fill,
			...otherProps
		} = this.props;

		const {
			prevFill,
		} = this.state;

		return (
			<use
				href="#hex"
				fill={prevFill || fill}
				{...otherProps}
			>
				{prevFill && prevFill !== fill &&
					<animate
						ref={ref => this.animation = ref}
						attributeName="fill"
						from={prevFill}
						to={fill}
						begin="indefinite"
						dur="0.8s"
						fill="freeze"
						repeatCount="1"
						calcMode="spline"
						keyTimes="0; 1"
						keySplines=".25 .1 .25 1"
					/>
				}
			</use>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/HexGrid.js